<template>
    <Teleport :to="headerMenuElement">
        <div class="flex items-center">
            <DButton
                data-cy="news-open-filters-button"
                theme="transparent"
                icon="ph:sliders-horizontal"
                class="mr-2"
                :disabled="loading || refreshingWidgetPage"
                :indicator="!!filterCount"
                :indicator-number="filterCount"
                indicator-color="brand"
                @click="openFilters"
            />
        </div>
    </Teleport>

    <SimpleWidgetPageWithSwitcher
        :widget-page-id="widgetPageId"
        :widget-type="widgetPage.type"
        :dependencies="dependencies"
        :placeholder-item-count="5"
        :loading="waitingForFilterLoad"
        @widget-page-id-change="emit('widgetPageIdChange', $event)"
    >
        <template #placeholder-item>
            <div class="py-4 space-y-2 transition-opacity">
                <UIGhostLoader type="h3" />
                <UIGhostLoader
                    type="h2 mb-2"
                    no-margin
                />
                <UIGhostLoader
                    type="p"
                    no-margin
                />
            </div>
        </template>
        <NewsItem
            v-for="(item, index) in displayItems"
            :key="index"
            :item="item"
            :large="index === 0"
            :is-last="index === displayItems.length - 1"
        />
    </SimpleWidgetPageWithSwitcher>
</template>

<script setup>
    import dayjs from '@/dayjs'
    import { differenceBy, isEmpty, orderBy, transform } from 'lodash'
    import { DButton } from '@digistorm/spark'

    import dependencyStatus from '@/constants/dependencyStatus'
    import widgetType from '@/constants/widgetType'
    import { useWidgetsStore } from '@/stores/widgets'
    import { useWidgetPagesStore } from '@/stores/widgetPages'
    import FiltersModal from '@/components/next-gen/widget-pages/shared/FiltersModal.vue'
    import NewsItem from '@/components/next-gen/widgets/news/NewsItem.vue'
    import { modalController } from '@ionic/vue'
    import { useWidgetFilters } from '@/composables/widgetFilters'

    const headerMenu = inject('headerMenu', null)

    const emit = defineEmits(['widgetPageIdChange'])

    const props = defineProps({
        widgetPageId: String,
    })

    const widgetsStore = useWidgetsStore()
    const widgetPagesStore = useWidgetPagesStore()

    const widgetFilterHelpers = useWidgetFilterHelpers()

    const { widgetPages } = storeToRefs(widgetPagesStore)

    const waitingForFilterLoad = ref(true)
    const shouldRefreshFilters = ref(false)

    const widgetPageMetadata = computed(() => widgetPages.value?.[props.widgetPageId])
    const widgetPage = computed(() => widgetPageMetadata.value?.data)

    const { selectedFilters, filterGroups, filterCount, fetchFilters, updateSelectedFilters, setFilterUrlWithoutFetching } = useWidgetFilters()

    const headerMenuElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-menu')
    })

    const dependencies = computed(() => {
        // In some cases we want to wait for filter load before calculating dependencies to prevent duplicate load calls
        if (waitingForFilterLoad.value) {
            return []
        }

        // Transform dependencies to include active filters and date range
        return transform(widgetPage.value?.dependencies ?? [], (dependencies, dependency) => {
            const resolvedDependency = widgetFilterHelpers.applyFiltersToDependency(
                filterGroups.value,
                selectedFilters.value,
                {
                    ...dependency,
                    original_url: dependency.url,
                },
            )

            // Filters may result in the dependency being removed (returned as null)
            if (!resolvedDependency) {
                return
            }

            dependencies.push(resolvedDependency)
        }, [])
    })

    const loading = computed(() => widgetsStore.isLoading(widgetType.news, dependencies.value))

    const itemsByDate = computed(() => {
        return orderBy(
            widgetsStore.items(widgetType.news, dependencies.value),
            (item) => {
                return dayjs(item.date).toDate()
            },
            'desc',
        )
    })

    const displayItems = computed(() => {
        return itemsByDate.value
    })

    const refreshingWidgetPage = computed(() => widgetPageMetadata.value?.status === dependencyStatus.pending)

    const onRefreshedWidgetPage = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        shouldRefreshFilters.value = true
    }

    const openFilters = async () => {
        const modal = await modalController.create({
            cssClass: 'ds-modal-fullscreen',
            component: FiltersModal,
            componentProps: {
                title: 'Filter',
                filterGroups: filterGroups.value,
                selectedFilters: selectedFilters.value,
                showDividers: false,
                applyChanges: async (value) => {
                    await updateSelectedFilters(value)
                },
            },
        })
        await modal.present()
    }

    const onWidgetPageChange = async () => {
        if (!widgetPage.value) {
            return
        }

        waitingForFilterLoad.value = true

        try {
            const filtersUrl = widgetPage.value?.filters_url
            await setFilterUrlWithoutFetching(filtersUrl)

            // If there were selected filters in the preferences we should await fetching of filters so that dependencies aren't loaded twice
            if (!isEmpty(selectedFilters.value) && isEmpty(filterGroups.value)) {
                await fetchFilters(filtersUrl, shouldRefreshFilters.value)
                    .catch((e) => console.error('Error fetching filters on widget page metadata change', e))
            } else {
                fetchFilters(filtersUrl, shouldRefreshFilters.value)
            }

            shouldRefreshFilters.value = false
        } finally {
            waitingForFilterLoad.value = false
        }
    }

    watch(dependencies, (after, before) => {
        const unusedDependencies = differenceBy(before, after, (dep) => dep.url)
        widgetsStore.cancelPendingDependencies(widgetType.news, unusedDependencies)
    })

    watch(widgetPage, onWidgetPageChange)

    onMounted(() => {
        events.on('refreshed-widget-page', onRefreshedWidgetPage)

        onWidgetPageChange(widgetPageMetadata.value)
    })

    onUnmounted(() => {
        events.off('refreshed-widget-page', onRefreshedWidgetPage)
    })
</script>
